import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';
import { CartDiscountUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Handles applying a discount code.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;

    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;

    const discountCodeValue = discountCode.value.trim();
    if (!discountCodeValue) return;

    const abortController = this.#createAbortController();

    const existingDiscounts = this.#existingDiscounts();
    if (existingDiscounts.some((code) => code.toLowerCase() === discountCodeValue.toLowerCase())) return;

    cartDiscountError.classList.add('hidden');
    cartDiscountErrorDiscountCode.classList.add('hidden');
    cartDiscountErrorShipping.classList.add('hidden');

    const allDiscountCodes = [...existingDiscounts, discountCodeValue];
    const deferredPromise = CartDiscountUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartDiscountUpdateEvent({
        discountCodes: allDiscountCodes.map((code) => ({ code })),
        promise: deferredPromise.promise,
      })
    );

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: allDiscountCodes.join(','),
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      if (
        data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
          return discount.code === discountCodeValue && discount.applicable === false;
        })
      ) {
        discountCode.value = '';
        this.#handleDiscountError('discount_code');
        deferredPromise.resolve({
          cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
        });
        return;
      }

      const shippingOnly = this.#isShippingOnlyDiscount(data, discountCodeValue, existingDiscounts);
      const sectionHtml = await this.#syncShippingDiscountAttribute(data, abortController);

      deferredPromise.resolve({
        cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
      });

      discountCode.value = '';

      if (sectionHtml) {
        morphSection(this.dataset.sectionId, sectionHtml, {
          mode: this.closest('theme-drawer') ? 'hydration' : 'full',
        });
      }

      if (shippingOnly) {
        this.#revealShippingDiscountNotice(this.dataset.sectionId);
      }
    } catch (error) {
      deferredPromise.reject(error);
      if (error instanceof Error && error.name !== 'AbortError') {
        this.dispatchEvent(
          new CartErrorEvent({
            error: error.message || 'Failed to apply discount',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      }
    } finally {
      this.#activeFetch = null;
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.findIndex((code) => code.toLowerCase() === discountCode.toLowerCase());
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    const abortController = this.#createAbortController();
    const deferredPromise = CartDiscountUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartDiscountUpdateEvent({
        discountCodes: existingDiscounts.map((code) => ({ code })),
        promise: deferredPromise.promise,
      })
    );

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: existingDiscounts.join(','),
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();
      const sectionHtml = await this.#syncShippingDiscountAttribute(data, abortController);

      deferredPromise.resolve({
        cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
      });

      if (sectionHtml) {
        morphSection(this.dataset.sectionId, sectionHtml, {
          mode: this.closest('theme-drawer') ? 'hydration' : 'full',
        });
      }
    } catch (error) {
      deferredPromise.reject(error);
      if (error instanceof Error && error.name !== 'AbortError') {
        this.dispatchEvent(
          new CartErrorEvent({
            error: error.message || 'Failed to remove discount',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      }
    } finally {
      this.#activeFetch = null;
    }
  };

  /**
   * Persist shipping-only coupons on the cart so Liquid can zero the estimated shipping fee.
   * Returns section HTML to morph (from follow-up update when attribute changes, else original).
   *
   * Important: always re-send `discount` with this follow-up update. A bare attributes-only
   * `/cart/update.js` call can clear previously applied discount codes from the cart session,
   * so they never reach checkout even though the cart UI still shows them via attributes.
   *
   * @param {object} data - Cart AJAX response
   * @param {AbortController} abortController
   * @returns {Promise<string | null>}
   */
  async #syncShippingDiscountAttribute(data, abortController) {
    const shippingCodes = this.#getShippingDiscountCodes(data);
    const nextValue = shippingCodes.join(',');
    const currentValue = (this.dataset.shippingDiscountCodes || '').trim();
    const originalHtml = data.sections?.[this.dataset.sectionId] ?? null;
    const appliedCodes = this.#getApplicableDiscountCodes(data);

    if (nextValue === currentValue) {
      return originalHtml;
    }

    const config = fetchConfig('json', {
      body: JSON.stringify({
        discount: appliedCodes.join(','),
        attributes: {
          sr_shipping_discount_codes: nextValue,
        },
        sections: [this.dataset.sectionId],
      }),
    });

    const response = await fetch(Theme.routes.cart_update_url, {
      ...config,
      signal: abortController.signal,
    });

    const nextData = await response.json();
    this.dataset.shippingDiscountCodes = nextValue;
    return nextData.sections?.[this.dataset.sectionId] ?? originalHtml;
  }

  /**
   * @param {object} data
   * @returns {string[]}
   */
  #getApplicableDiscountCodes(data) {
    /** @type {string[]} */
    const codes = [];

    for (const discount of data.discount_codes || []) {
      if (!discount?.applicable || typeof discount.code !== 'string') continue;
      const code = discount.code.trim();
      if (code) codes.push(code);
    }

    return codes;
  }

  /**
   * Shipping-only codes are applicable on the cart but do not create product/order discount pills.
   *
   * @param {object} data
   * @returns {string[]}
   */
  #getShippingDiscountCodes(data) {
    const productCodes = this.#getProductDiscountCodeTitles(data);
    /** @type {string[]} */
    const shippingCodes = [];

    for (const discount of data.discount_codes || []) {
      if (!discount?.applicable || typeof discount.code !== 'string') continue;
      const code = discount.code.trim();
      if (!code) continue;
      const isProductCode = productCodes.some((title) => title.toLowerCase() === code.toLowerCase());
      if (!isProductCode) {
        shippingCodes.push(code);
      }
    }

    return shippingCodes;
  }

  /**
   * @param {object} data
   * @returns {string[]}
   */
  #getProductDiscountCodeTitles(data) {
    /** @type {string[]} */
    const titles = [];

    for (const application of data.cart_level_discount_applications || []) {
      if (application?.title) titles.push(String(application.title));
    }

    for (const item of data.items || []) {
      for (const discount of item.discounts || []) {
        if (discount?.title) titles.push(String(discount.title));
      }
    }

    return titles;
  }

  /**
   * @param {object} data
   * @param {string} discountCodeValue
   * @param {string[]} existingDiscounts
   * @returns {boolean}
   */
  #isShippingOnlyDiscount(data, discountCodeValue, existingDiscounts) {
    const newHtml = data.sections?.[this.dataset.sectionId];
    if (!newHtml) {
      return this.#getShippingDiscountCodes(data).some(
        (code) => code.toLowerCase() === discountCodeValue.toLowerCase()
      );
    }

    const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
    const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
    const discountCodes = section?.querySelectorAll('.cart-discount__pill') || [];
    const codes = Array.from(discountCodes)
      .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
      .filter(Boolean);

    // Before attribute sync, shipping codes are not in Liquid pills yet.
    const pillsUnchanged =
      codes.length === existingDiscounts.length &&
      codes.every((/** @type {string} */ code) =>
        existingDiscounts.some((existing) => existing.toLowerCase() === code.toLowerCase())
      );

    const applicable = data.discount_codes?.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
      return discount.code === discountCodeValue && discount.applicable === true;
    });

    return Boolean(pillsUnchanged && applicable);
  }

  /**
   * Handles invalid discount code errors.
   *
   * @param {'discount_code'} type
   */
  #handleDiscountError(type) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    cartDiscountErrorShipping.classList.add('hidden');
    const target = cartDiscountErrorDiscountCode;
    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');

    this.dispatchEvent(
      new CartErrorEvent({
        error: 'Invalid discount code',
        code: 'VALIDATION_CUSTOM',
      })
    );
  }

  /**
   * Confirms a shipping discount was applied (totals already reflect free shipping after morph).
   * Must run after morphSection, because morph replaces this component's DOM.
   * @param {string} sectionId
   */
  #revealShippingDiscountNotice(sectionId) {
    const root = document.getElementById(`shopify-section-${sectionId}`);
    if (!root) return;

    const error = root.querySelector('[ref="cartDiscountError"]');
    const shipping = root.querySelector('[ref="cartDiscountErrorShipping"]');
    const codeErr = root.querySelector('[ref="cartDiscountErrorDiscountCode"]');

    error?.classList.remove('hidden');
    shipping?.classList.remove('hidden');
    codeErr?.classList.add('hidden');
  }

  /**
   * Returns an array of existing discount codes from pills.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__pill');
    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        const code = pill.dataset.discountCode.trim();
        if (code) discountCodes.push(code);
      }
    }

    return discountCodes;
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}

/**
 * Codes shown as pills on the cart (manual coupons / shipping codes).
 * B2B volume pricing uses Shopify automatic discounts — no codes collected here.
 * @returns {string[]}
 */
function collectCheckoutDiscountCodes() {
  /** @type {string[]} */
  const codes = [];
  const seen = new Set();

  /** @param {string | undefined | null} raw */
  const add = (raw) => {
    const code = String(raw || '').trim();
    if (!code) return;
    // Skip legacy / deactivated B2B code pattern if still shown as a pill.
    if (/^SRB2B/i.test(code) || /特約店/i.test(code)) return;
    const key = code.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    codes.push(code);
  };

  for (const pill of document.querySelectorAll('.cart-discount__pill[data-discount-code]')) {
    if (!(pill instanceof HTMLElement)) continue;
    add(pill.dataset.discountCode);
  }

  return codes;
}

/**
 * Merge DOM codes with whatever Shopify currently has as applicable on the cart.
 * @param {string[]} seed
 * @returns {Promise<string[]>}
 */
async function mergeApplicableCartCodes(seed) {
  const codes = [...seed];
  const seen = new Set(codes.map((c) => c.toLowerCase()));
  const root = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');

  try {
    const res = await fetch(`${root}cart.js`, { credentials: 'same-origin' });
    if (!res.ok) return codes;
    const cart = await res.json();
    for (const entry of cart.discount_codes || []) {
      if (!entry?.applicable || typeof entry.code !== 'string') continue;
      const code = entry.code.trim();
      if (!code || /^SRB2B/i.test(code)) continue;
      const key = code.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      codes.push(code);
    }
  } catch (error) {
    console.warn('[cart-discount] cart.js code merge failed', error);
  }

  return codes;
}

/**
 * Before leaving for Checkout, re-apply visible (non-B2B) discount codes and
 * open Checkout with `?discount=` so marketing codes survive session updates.
 * 特約店価格は自動割引で付与されるため、ここでは扱わない。
 *
 * @param {MouseEvent | SubmitEvent} event
 */
async function ensureDiscountsForCheckout(event) {
  const target = event.target;
  if (!(target instanceof Element) && !(target instanceof HTMLFormElement)) return;

  const checkoutButton =
    target instanceof Element
      ? target.closest('#checkout, button[name="checkout"], input[name="checkout"], .sr-cart-mobile-bar-btn')
      : null;

  // Guest account-gate links (register/login) — do not intercept.
  if (
    checkoutButton instanceof HTMLAnchorElement &&
    !checkoutButton.matches('button[name="checkout"], input[name="checkout"]')
  ) {
    return;
  }

  if (event instanceof SubmitEvent) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.closest('cart-items-component, .cart-page, .cart-drawer')) return;
    const submitter = event.submitter;
    const isCheckoutSubmit =
      (submitter instanceof HTMLButtonElement || submitter instanceof HTMLInputElement) &&
      submitter.name === 'checkout';
    if (!isCheckoutSubmit && !form.querySelector('[name="checkout"]')) return;
  } else if (checkoutButton) {
    if (!checkoutButton.closest('cart-items-component, .cart-page, .cart-drawer, .sr-cart-mobile-bar')) return;
    if (
      (checkoutButton instanceof HTMLButtonElement || checkoutButton instanceof HTMLInputElement) &&
      checkoutButton.disabled
    ) {
      return;
    }
  } else {
    return;
  }

  const pendingEl = checkoutButton instanceof HTMLElement ? checkoutButton : document.getElementById('checkout');
  if (pendingEl instanceof HTMLElement && pendingEl.dataset.srCheckoutPending === 'true') {
    event.preventDefault();
    event.stopPropagation();
    return;
  }

  event.preventDefault();
  event.stopPropagation();

  if (pendingEl instanceof HTMLElement) {
    pendingEl.dataset.srCheckoutPending = 'true';
  }

  const root = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
  const checkoutPath = `${root}checkout`;

  try {
    const codes = await mergeApplicableCartCodes(collectCheckoutDiscountCodes());

    if (codes.length > 0) {
      const config = fetchConfig('json', {
        body: JSON.stringify({ discount: codes.join(',') }),
      });
      await fetch(Theme.routes.cart_update_url, config);
      window.location.href = `${checkoutPath}?discount=${encodeURIComponent(codes.join(','))}`;
      return;
    }
  } catch (error) {
    console.warn('[cart-discount] Failed to re-apply codes before checkout', error);
  }

  window.location.href = checkoutPath;
}

document.addEventListener(
  'click',
  (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (!target.closest('#checkout, button[name="checkout"], input[name="checkout"], .sr-cart-mobile-bar-btn')) {
      return;
    }
    ensureDiscountsForCheckout(event);
  },
  true
);

document.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.closest('cart-items-component, .cart-page, .cart-drawer')) return;
    const submitter = event.submitter;
    const isCheckout =
      (submitter instanceof HTMLButtonElement || submitter instanceof HTMLInputElement) &&
      submitter.name === 'checkout';
    if (!isCheckout) return;
    ensureDiscountsForCheckout(event);
  },
  true
);
